import { useEffect, useState, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Plus, Trash2, FileText, Video, Link as LinkIcon } from 'lucide-react';

const blankLink = (url = '', title = '', kind = 'web') => ({ url, title, kind });

export default function CatalogAttachmentsTab({ product, onSaved }) {
  const [videos, setVideos] = useState([]);
  const [pdfs, setPdfs] = useState([]);
  const [urls, setUrls] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const pdfRef = useRef(null);

  useEffect(() => {
    setVideos((product?.videos || []).map((v) => blankLink(v.url || v, v.title || '', v.kind || 'video')));
    setPdfs((product?.pdfs || []).map((v) => blankLink(v.url || v, v.title || '', 'pdf')));
    const extraYt = (product?.youtube_urls || []).map((u) => blankLink(u, '', 'youtube'));
    const merged = [...(product?.urls || []).map((v) => blankLink(v.url || v, v.title || '', v.kind || 'web')), ...extraYt];
    const seen = new Set();
    setUrls(merged.filter((x) => {
      if (!x.url || seen.has(x.url)) return false;
      seen.add(x.url);
      return true;
    }));
  }, [product]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/products/${product.id}`, { videos, pdfs, urls });
      toast.success('Material del producto guardado');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/upload/file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = r.data?.url;
      if (url) setPdfs((prev) => [...prev, blankLink(url, file.name, 'pdf')]);
      toast.success('PDF subido');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al subir PDF');
    } finally {
      setUploading(false);
      if (pdfRef.current) pdfRef.current.value = '';
    }
  };

  const LinkList = ({ title, icon: Icon, items, setItems, kind, testId, placeholder }) => (
    <Card className="bg-card/50 border-white/5">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-white flex items-center gap-2">
          <Icon className="w-4 h-4" /> {title}
        </CardTitle>
        <CardDescription className="text-[11px]">La IA puede enviar esto por WhatsApp.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((it, i) => (
          <div key={`${kind}-${i}`} className="grid grid-cols-[1fr_1fr_auto] gap-2">
            <Input
              value={it.title}
              placeholder="Título"
              className="bg-black/20 border-white/10 text-xs"
              onChange={(e) => setItems((prev) => prev.map((x, n) => (n === i ? { ...x, title: e.target.value } : x)))}
            />
            <Input
              value={it.url}
              placeholder={placeholder}
              className="bg-black/20 border-white/10 text-xs"
              onChange={(e) => setItems((prev) => prev.map((x, n) => (n === i ? { ...x, url: e.target.value } : x)))}
              data-testid={`${testId}-url-${i}`}
            />
            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => setItems((prev) => prev.filter((_, n) => n !== i))}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
        <Button size="sm" variant="outline" className="text-xs" onClick={() => setItems((prev) => [...prev, blankLink('', '', kind)])} data-testid={`${testId}-add`}>
          <Plus className="w-3 h-3 mr-1" />Agregar
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-3" data-testid="catalog-attachments-tab">
      <LinkList title="Videos / YouTube" icon={Video} items={videos} setItems={setVideos} kind="video" testId="cat-video" placeholder="https://youtu.be/..." />
      <Card className="bg-card/50 border-white/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-white flex items-center gap-2"><FileText className="w-4 h-4" /> PDFs / fichas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {pdfs.map((it, i) => (
            <div key={`pdf-${i}`} className="grid grid-cols-[1fr_1fr_auto] gap-2">
              <Input value={it.title} placeholder="Nombre" className="bg-black/20 border-white/10 text-xs" onChange={(e) => setPdfs((prev) => prev.map((x, n) => (n === i ? { ...x, title: e.target.value } : x)))} />
              <Input value={it.url} placeholder="https://..." className="bg-black/20 border-white/10 text-xs" onChange={(e) => setPdfs((prev) => prev.map((x, n) => (n === i ? { ...x, url: e.target.value } : x)))} />
              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => setPdfs((prev) => prev.filter((_, n) => n !== i))}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="text-xs" onClick={() => setPdfs((prev) => [...prev, blankLink('', '', 'pdf')])}><Plus className="w-3 h-3 mr-1" />URL</Button>
            <Button size="sm" variant="outline" className="text-xs" disabled={uploading} onClick={() => pdfRef.current?.click()}>
              {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />}Subir PDF
            </Button>
            <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadPdf(e.target.files?.[0])} />
          </div>
        </CardContent>
      </Card>
      <LinkList title="Links (web, landing, formularios)" icon={LinkIcon} items={urls} setItems={setUrls} kind="web" testId="cat-url" placeholder="https://..." />
      <Button size="sm" className="text-xs" onClick={save} disabled={saving} data-testid="catalog-attachments-save">
        {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}Guardar material
      </Button>
    </div>
  );
}
